import { useState, useEffect, useRef } from 'react';
import { supabase, Product } from '../lib/supabase';

export function useProducts(autoSync = false, syncInterval = 30000) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchProducts();

    // Setup auto-sync if enabled
    if (autoSync) {
      // Sync when page becomes visible
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          fetchProducts(true);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Setup interval for auto-sync
      intervalRef.current = window.setInterval(() => {
        if (!document.hidden) {
          fetchProducts(true);
        }
      }, syncInterval);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [autoSync, syncInterval]);

  const fetchProducts = async (isAutoSync = false) => {
    try {
      // Only show loading spinner on initial load, not on auto-sync
      if (isInitialLoad.current) {
        setLoading(true);
        isInitialLoad.current = false;
      } else if (isAutoSync) {
        setIsSyncing(true);
      } else {
        setLoading(true);
      }
      
      // Fetch from Shopify via Edge Function
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/get-all-products`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch products from Shopify');
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error);
      }
      
      setProducts(data.products || []);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch products');
      setProducts([]);
    } finally {
      setLoading(false);
      setIsSyncing(false);
    }
  };

  const addProduct = async (productData: Omit<Product, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('products')
        .insert([{
          ...productData,
          created_by: user.id,
          updated_by: user.id,
        }])
        .select()
        .single();

      if (error) throw error;

      // Log the change
      await logChange('products', data.id, 'INSERT', null, data, user.id);
      
      setProducts(prev => [data, ...prev]);
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get old data for logging
      const { data: oldData } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      const { data, error } = await supabase
        .from('products')
        .update({
          ...updates,
          updated_by: user.id,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Log the change
      await logChange('products', id, 'UPDATE', oldData, data, user.id);

      setProducts(prev => prev.map(p => p.id === id ? data : p));
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Get old data for logging
      const { data: oldData } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();

      const { error } = await supabase
        .from('products')
        .update({ is_active: false, updated_by: user.id })
        .eq('id', id);

      if (error) throw error;

      // Log the change
      await logChange('products', id, 'DELETE', oldData, null, user.id);

      setProducts(prev => prev.filter(p => p.id !== id));
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const logChange = async (tableName: string, recordId: string, action: string, oldData: any, newData: any, userId: string) => {
    try {
      await supabase
        .from('change_logs')
        .insert([{
          table_name: tableName,
          record_id: recordId,
          action,
          old_data: oldData,
          new_data: newData,
          changed_by: userId,
        }]);
    } catch (error) {
      console.error('Error logging change:', error);
    }
  };

  return {
    products,
    loading,
    error,
    isSyncing,
    fetchProducts: () => fetchProducts(false),
    refetch: () => fetchProducts(false),
    addProduct,
    updateProduct,
    deleteProduct,
  };
}